import { graphql } from "generated/gql";

export const ClassificationFieldsFragment = graphql(/* GraphQL */ `
  fragment ClassificationFields on Classification {
    class
    family
    genus
    infraspecies
    kingdom
    order
    phylum
    section
    series
    species
    subclass
    subfamily
    subgenus
    subkingdom
    suborder
    subphylum
    subsection
    subseries
    subspecies
    subtribe
    superorder
    tribe
  }
`);

export const ScientificNameFieldsFragment = graphql(/* GraphQL */ `
  fragment ScientificNameFields on ScientificName {
    id
    scientificName
    authorship
    status
    hybridType
    rank
    nameSource
    nameSourceUrl
    taxon {
      id
      nativeDistribution
      climate
      globalRedListStatus
      globalRedListSourceUrl
      lifeForms {
        nodes {
          id
          name
        }
      }
    }
    classification {
      ...ClassificationFields
    }
  }
`);

export const TaxonFieldsFragment = graphql(/* GraphQL */ `
  fragment TaxonFields on Taxon {
    id
    archived
    organisation {
      id
      taxaInterpretationEnabled
    }
    createdBy {
      id
    }
    createdAt
    updatedBy {
      id
    }
    updatedAt
    scientificName
    commonName
    authorship
    notes
    notesData
    fullyValidated
    tagsConnection {
      nodes {
        ...RecordTagFields
      }
    }
    interpretationDescription
    nativeDistribution
    sharedScientificName {
      ...ScientificNameFields
    }
    historyConnection {
      nodes {
        taxonId
        taxon {
          id
          scientificName
          archived
          createdAt
          organisationId
          updatedAt
        }
        createdAt
        createdById
        createdBy {
          id
          initials
          email
          givenName
          familyName
          avatarUrl
          isActivated
          accessRole
          deactivatedAt
        }
        fromSharedScientificName {
          ...ScientificNameFields
        }
        toSharedScientificName {
          ...ScientificNameFields
        }
        wasArchived
        wasRestored
        fromScientificName
        toScientificName
        fromCommonName
        toCommonName
        fromAuthorship
        toAuthorship
        fromNotes
        toNotes
        fromInterpretationDescription
        toInterpretationDescription
        fromNativeDistribution
        toNativeDistribution
      }
    }
    accessions {
      id
      accessionNumber
      accessionDate
      scientificName
      collectionSite {
        id
        name
        urlSlug
      }
      provenanceCategory {
        name
        codeId
      }
      plantMaterial(filters: $relatedMaterialsFilters) {
        id
        archived
        status
        qualifier
        materialGroup
        position {
          latitude
          longitude
          elevation
        }
        accession {
          scientificName
          accessionNumber
          collectionSite {
            id
            name
            urlSlug
          }
        }
        collectionSiteLocation {
          id
          name
          code
          archived
        }
        primaryImage {
          ...MaterialImageFields
        }
        images {
          ...MaterialImageFields
        }
      }
    }
  }
`);

export const getTaxonDocument = graphql(/* GraphQL */ `
  query GetTaxon($id: ID!, $relatedMaterialsFilters: PlantMaterialFilter) {
    taxon(id: $id) {
      ...TaxonFields
    }
  }
`);

export const getSharedScientificNameDocument = graphql(/* GraphQL */ `
  query GetSharedScientificName($id: ID!) {
    sharedScientificName(id: $id) {
      ...ScientificNameFields
    }
  }
`);

export const archiveTaxonDocument = graphql(/* GraphQL */ `
  mutation ArchiveTaxon($id: ID!) {
    archiveTaxon(id: $id) {
      success
    }
  }
`);

export const restoreTaxonDocument = graphql(/* GraphQL */ `
  mutation RestoreTaxon(
    $id: ID!
    $relatedMaterialsFilters: PlantMaterialFilter
  ) {
    restoreTaxon(id: $id) {
      success
      errors {
        ... on DuplicateScientificNameError {
          __typename
          message
        }
      }
      taxon {
        ...TaxonFields
      }
    }
  }
`);

export const updateTaxonDocument = graphql(/* GraphQL */ `
  mutation UpdateTaxon($id: ID!, $input: UpdateTaxonInput!) {
    updateTaxon(id: $id, input: $input) {
      success
      errors {
        ... on DuplicateScientificNameError {
          __typename
          message
        }
      }
      taxon {
        id
        scientificName
      }
    }
  }
`);

export const validateArchiveTaxaDocument = graphql(/* GraphQL */ `
  query ValidateArchiveTaxa($ids: [ID!]!) {
    validateBatchArchiveTaxa(ids: $ids) {
      success
      errors {
        ... on AssociatedAccessionsError {
          __typename
          message
          taxa {
            id
          }
        }
      }
      taxa {
        id
        archived
      }
    }
  }
`);

export const archiveTaxaDocument = graphql(/* GraphQL */ `
  mutation BatchArchiveTaxa($ids: [ID!]!) {
    batchArchiveTaxa(ids: $ids) {
      success
      errors {
        ... on AssociatedAccessionsError {
          __typename
          message
          taxa {
            id
          }
        }
      }
      taxa {
        id
        archived
      }
    }
  }
`);

export const addTagToTaxaDocument = graphql(/* GraphQL */ `
  mutation AddTagToTaxa($ids: [ID!]!, $tagId: ID!) {
    addTagTaxa(ids: $ids, tagId: $tagId) {
      success
      errors {
        ... on GenericError {
          message
        }
      }
      taxa {
        id
      }
    }
  }
`);

export const removeTagFromTaxaDocument = graphql(/* GraphQL */ `
  mutation RemoveTagFromTaxa($ids: [ID!]!, $tagId: ID!) {
    removeTagTaxa(ids: $ids, tagId: $tagId) {
      success
      errors {
        ... on GenericError {
          message
        }
      }
      taxa {
        id
      }
    }
  }
`);
